import { apiClient } from '../../apiHelper';
import { useCreate, useEdit, useSingleFetch } from '../react-query';
import { User } from '../users/users';
import { usersQueryKeys } from '../users/users-query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { message } from 'antd';
import { messageI18n } from '../../i18n/toastI18n';
interface LoginCredentials {
  email: string;
  password: string;
}

interface PasswordResetRequest {
  email: string;
}

interface PasswordResetConfirm {
  code: string;
  email: string;
  new_password: string;
}

interface PasswordChange {
  current_password: string;
  new_password: string;
}

interface ForcedPasswordChangeConfirm {
  // Omitted on first login of an account created with a temporary password: no code
  // was sent, because there is no verified channel to send one to yet.
  verification_code?: string;
  new_password: string;
}

interface RefreshTokenRequest {
  refresh_token: string;
}

interface AuthResponse {
  user: {
    id: number;
    uid: string;
    name: string;
    email: string;
    username: string;
    avatar?: string;
    role: string;
    is_active: boolean;
    last_login?: Date;
    roles?: string[];
  };
  tokens: {
    access_token: string;
    refresh_token: string;
  };
}

interface SamlSettings {
  acs_url?: string,
  entity_id?: string,
  certificate?: string
}

// Define query keys for auth operations
const authQueryKeys = {
  all: ['auth'],
  // Same key as useMe(): one cache entry for GET /users/me.
  profile: () => usersQueryKeys.detail('me'),
  login: () => [...authQueryKeys.all, 'login'],
  refreshToken: () => [...authQueryKeys.all, 'refresh'],
  passwordReset: () => [...authQueryKeys.all, 'password-reset'],
  saml: () => [...authQueryKeys.all, 'saml'],
  samlSettings: () => [...authQueryKeys.saml(), 'settings']
};

export function useProfile(config = {}) {
  const getMeFn = async (): Promise<User> => {
    const response = await apiClient.get<User>(`/users/me`);
    return response?.data;
  };

  return useSingleFetch(getMeFn, authQueryKeys.profile(), 'me', config);
}

export function useLogin() {
  const loginFn = async (
    credentials: LoginCredentials & { totp_code?: string }
  ): Promise<
    | AuthResponse
    | { twofa_required: boolean }
    | {
        force_password_change_required: boolean;
        verification_code_required: boolean;
      }
    | Record<string, unknown>
  > => {
    console.log('============ Auth API: Attempting login with credentials', {
      email: credentials.email,
    });
    try {
      const response = await apiClient.post('login', credentials);
      const payload = response?.data ?? response;
      // If backend responds with 2fa_required, return that
      if (payload?.twofa_required) {
        return { twofa_required: true };
      }
      if (payload?.force_password_change_required) {
        return {
          force_password_change_required: true,
          // Whether the change also needs the emailed/WhatsApp'd code: true when an
          // admin reset an established account, false on first login of one created
          // with a temporary password. Absent means a backend that predates the
          // distinction, where the code was always sent — so require it.
          verification_code_required:
            payload?.verification_code_required ?? true,
        };
      }
      return payload;
    } catch (error) {
      console.error('============ Auth API: Login failed with error', error);
      throw error;
    }
  };

  return useCreate(
    loginFn,
    authQueryKeys.login,
    'Login failed',
    'Login successful'
  );
}

export function useRefreshToken() {
  const refreshTokenFn = async (
    data: RefreshTokenRequest
  ): Promise<AuthResponse> => {
    const response = await apiClient.post('refresh', data);
    return response.data;
  };

  return useCreate(
    refreshTokenFn,
    authQueryKeys.refreshToken(),
    'Token refresh failed',
    'Token refreshed successfully'
  );
}

export function useRequestPasswordReset() {
  const requestPasswordResetFn = async (
    data: PasswordResetRequest
  ): Promise<void> => {
    const response = await apiClient.post('password-reset/request', data);
  };

  return useCreate(
    requestPasswordResetFn,
    authQueryKeys.passwordReset(),
    'Password reset request failed',
    'Password reset email sent successfully'
  );
}

export function useConfirmPasswordReset() {
  const confirmPasswordResetFn = async (
    data: PasswordResetConfirm
  ): Promise<void> => {
    const response = await apiClient.post('password-reset/confirm', data);
  };

  return useCreate(
    confirmPasswordResetFn,
    authQueryKeys.passwordReset(),
    'Password reset failed',
    'Password reset successful'
  );
}

export function useChangePassword() {
  const changePasswordFn = async (data: PasswordChange): Promise<void> => {
    const response = await apiClient.post('password/change', data);
  };

  return useEdit(
    changePasswordFn,
    authQueryKeys.all,
    'Password change failed',
    'Password changed successfully'
  );
}

export function useLogout() {
  const logoutFn = async (): Promise<void> => {
    const response = await apiClient.post('logout');
  };

  return useCreate(
    logoutFn,
    authQueryKeys.all,
    'Logout failed',
    'Logged out successfully'
  );
}

export function useRefreshAccessToken() {
  const refreshAccessTokenFn = async (): Promise<void> => {
    const response = await apiClient.post('refresh-token', {
      refresh_token: localStorage.getItem('refresh_token'),
    });
  };

  return useCreate(
    refreshAccessTokenFn,
    authQueryKeys.all,
    'Token refresh failed',
    'Token refreshed successfully'
  );
}

export function useInitiatePasswordChange(
  onSuccess?: (response: any, data: any) => void,
  onError?: (error: any, data: any) => void,
  notification = true
) {
  const queryClient = useQueryClient();

  const initiatePasswordChangeFn = async (data: { new_password: string }) => {
    const response = await apiClient.post(`/initiate-password-change`, data);
    return response;
  };

  return useMutation({
    mutationFn: initiatePasswordChangeFn,
    onSuccess: (response, data) => {
      if (onSuccess) {
        onSuccess(response, data);
      }
      if (notification) {
        messageI18n.success('auth.verificationCodeSent');
      }
    },
    onError: (error: any, data) => {
      const errorResponse = error.response?.data;
      if (onError) {
        onError(error, data);
      }
      if (notification) {
        message.error(
          errorResponse?.message || 'Error while initiating password change'
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.all });
    },
  });
}

export function useConfirmPasswordChange() {
  const confirmPasswordChangeFn = async (data: {
    verification_code: string;
    totp_code?: string;
  }): Promise<void> => {
    const response = await apiClient.post(`/confirm-password-change`, data);
    return response;
  };

  return useEdit(
    confirmPasswordChangeFn,
    authQueryKeys.all,
    'Error while confirming password change',
    'Password changed successfully'
  );
}

export function useCompleteTemporaryPasswordChange() {
  const completePasswordChangeFn = async (
    data: ForcedPasswordChangeConfirm
  ): Promise<AuthResponse> => {
    const response = await apiClient.post(
      '/complete-temporary-password-change',
      data
    );
    return response.data ?? response;
  };

  return useEdit(
    completePasswordChangeFn,
    authQueryKeys.all,
    'Error while changing password',
    'Password changed successfully'
  );
}

export function useSamlSettings(config = {}) {
  const getSamlSettingsFn = async (): Promise<SamlSettings> => {
    try {
      const response = await apiClient.get<SamlSettings>('/settings/sso');
      return response;
    } catch (error) {
      console.error('============ Auth API: Failed to retrieve SAML settings', error);
      throw error;
    }
  };

  return useSingleFetch(
    getSamlSettingsFn, 
    authQueryKeys.samlSettings(), 
    'saml-settings',
    config
  );
}

export function useUpdateSamlSettings() {
  const queryClient = useQueryClient();
  
  const updateSamlSettingsFn = async (data: {
    acs_url: string;
    entity_id: string;
    certificate: string;
  }): Promise<SamlSettings> => {
    try {
      const response = await apiClient.post<SamlSettings>('/settings/sso', data);
      return response.data;
    } catch (error) {
      console.error('============ Auth API: Failed to update SAML settings', error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: updateSamlSettingsFn,
    onSuccess: () => {
      messageI18n.success('auth.ssoConfigUpdated');
      queryClient.invalidateQueries({ queryKey: authQueryKeys.samlSettings() });
    },
    onError: (error: any) => {
      messageI18n.error('auth.ssoConfigUpdateFailed');
    }
  });
}

export function useSamlSetToken() {
  const queryClient = useQueryClient();
  
  const samlSetTokenFn = async (data: { token: string; totp_code?: string }): Promise<AuthResponse | any> => {
    try {
      const response = await apiClient.post('/saml/set', data);
      return response;
    } catch (error) {
      console.error('============ Auth API: SAML token setting failed', error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: samlSetTokenFn,
    onSuccess: (data: AuthResponse) => {
      queryClient.invalidateQueries({ queryKey: authQueryKeys.all });
    },
    onError: (error: any) => {
      messageI18n.error('auth.ssoAuthFailed');
      console.error('============ Auth API: SAML set token error:', error);
    }
  });
}
